import React, { useState, useMemo } from 'react';
import './Grid.css';
import { FaSearch, FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

/**
 * Universal Data Grid & Table Component for Aranyasetu
 *
 * @param {Array} columns - Array of column definition objects:
 *   - header: string | ReactNode (Column title)
 *   - accessor: string | ((row) => any) (Field key or data extractor)
 *   - render: (value, row, index) => ReactNode (Custom cell renderer)
 *   - align: 'left' | 'center' | 'right' (Default: 'left')
 *   - width: string (e.g. '15%', '180px')
 *   - sortable: boolean (Enables column click sorting)
 *   - headerStyle / cellStyle: object (Custom inline styles)
 *   - className / headerClassName: string
 *
 * @param {Array} data - Array of row data objects
 * @param {string | ((row, index) => string|number)} rowKey - Unique key for each row
 * @param {string} variant - 'forest' (Green gradient) | 'navy' (Navy gradient) | 'clean' (Default: 'forest')
 * @param {boolean} pagination - Enable client-side pagination (Default: false)
 * @param {number} pageSize - Number of items per page (Default: 10)
 * @param {Array<number>} pageSizeOptions - Rows per page options (Default: [5, 10, 20, 50])
 * @param {number} page - Controlled page index (1-based)
 * @param {Function} onPageChange - Callback when page changes
 * @param {boolean} searchable - Enable local text search across data (Default: false)
 * @param {string} searchPlaceholder - Placeholder for search input
 * @param {string | number} maxHeight - Optional max height for scrollable table body (e.g. '320px', 400)
 * @param {string | number} height - Optional fixed height for table wrapper
 * @param {string | ReactNode | Array} footer - Footer definition:
 *   - Array of { content, align, colSpan, style, className }
 *   - OR custom ReactNode / render function
 * @param {boolean} loading - Displays loading spinner overlay/cell
 * @param {string | ReactNode} emptyMessage - Message displayed when data is empty
 * @param {string} title - Optional title displayed in table header bar
 * @param {string} subtitle - Optional subtitle displayed in table header bar
 * @param {ReactNode} headerActions - Optional custom buttons/actions on top right
 * @param {boolean} striped - Alternate row background shading (Default: true)
 * @param {boolean} hoverable - Highlight row on hover (Default: true)
 * @param {boolean} stickyHeader - Make table head stick on scroll (Default: true)
 * @param {boolean} stickyFooter - Make table footer stick on scroll (Default: true)
 * @param {string} className - Additional CSS class for outer container
 * @param {string} wrapperClassName - Additional CSS class for table wrapper
 * @param {string} tableClassName - Additional CSS class for <table> element
 * @param {object} style - Additional style for container
 * @param {object} wrapperStyle - Additional style for table wrapper
 */
const Grid = ({
  columns = [],
  data = [],
  rowKey,
  variant = 'forest',
  pagination = false,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  page: controlledPage,
  onPageChange,
  searchable = false,
  searchPlaceholder = 'Search records...',
  searchValue: controlledSearch,
  onSearchChange,
  maxHeight,
  height,
  footer,
  loading = false,
  emptyMessage = 'No records found',
  title,
  subtitle,
  headerActions,
  striped = true,
  hoverable = true,
  stickyHeader = true,
  stickyFooter = true,
  className = '',
  wrapperClassName = '',
  tableClassName = '',
  style = {},
  wrapperStyle = {},
}) => {
  // Internal search state (if uncontrolled)
  const [internalSearch, setInternalSearch] = useState('');
  const activeSearch = controlledSearch !== undefined ? controlledSearch : internalSearch;

  // Internal sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

  // Internal pagination state (if uncontrolled)
  const [internalPage, setInternalPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const activePage = controlledPage !== undefined ? controlledPage : internalPage;

  // Search input handler
  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (onSearchChange) {
      onSearchChange(val);
    } else {
      setInternalSearch(val);
    }
    // Reset to page 1 on search
    if (controlledPage === undefined) {
      setInternalPage(1);
    } else if (onPageChange) {
      onPageChange(1);
    }
  };

  // Sort handler
  const handleSort = (col) => {
    if (!col.sortable) return;
    const colKey = typeof col.accessor === 'string' ? col.accessor : col.header;
    if (sortColumn === colKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(colKey);
      setSortDirection('asc');
    }
  };

  // Filtered Data based on search
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!activeSearch || activeSearch.trim() === '') return data;

    const query = activeSearch.toLowerCase().trim();
    return data.filter((row) => {
      return columns.some((col) => {
        let val;
        if (typeof col.accessor === 'function') {
          val = col.accessor(row);
        } else if (col.accessor) {
          val = row[col.accessor];
        }
        if (val == null) return false;
        return String(val).toLowerCase().includes(query);
      });
    });
  }, [data, activeSearch, columns]);

  // Sorted Data
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    const targetCol = columns.find(
      (c) => (typeof c.accessor === 'string' ? c.accessor : c.header) === sortColumn
    );
    if (!targetCol) return filteredData;

    return [...filteredData].sort((a, b) => {
      let valA = typeof targetCol.accessor === 'function' ? targetCol.accessor(a) : a[targetCol.accessor];
      let valB = typeof targetCol.accessor === 'function' ? targetCol.accessor(b) : b[targetCol.accessor];

      if (valA == null) valA = '';
      if (valB == null) valB = '';

      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }
      return sortDirection === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [filteredData, sortColumn, sortDirection, columns]);

  // Paginated Data
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPageSafe = Math.min(Math.max(1, activePage), totalPages);

  const displayData = useMemo(() => {
    if (!pagination) return sortedData;
    const startIdx = (currentPageSafe - 1) * pageSize;
    return sortedData.slice(startIdx, startIdx + pageSize);
  }, [sortedData, pagination, currentPageSafe, pageSize]);

  // Page navigation helper
  const goToPage = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (onPageChange) {
      onPageChange(target);
    } else {
      setInternalPage(target);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    goToPage(1);
  };

  // Helper to extract row key
  const getRowKey = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row, index);
    if (typeof rowKey === 'string' && row[rowKey] !== undefined) return row[rowKey];
    return row.id !== undefined ? row.id : index;
  };

  // Alignment class helper
  const getAlignClass = (align) => {
    if (align === 'center') return 'aranya-grid-align-center';
    if (align === 'right') return 'aranya-grid-align-right';
    return 'aranya-grid-align-left';
  };

  // Build Pagination Range Array
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPageSafe - 2);
      let end = Math.min(totalPages, currentPageSafe + 2);
      if (start === 1) end = Math.min(totalPages, maxVisible);
      if (end === totalPages) start = Math.max(1, totalPages - maxVisible + 1);

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startRecord = totalItems === 0 ? 0 : (currentPageSafe - 1) * pageSize + 1;
  const endRecord = Math.min(currentPageSafe * pageSize, totalItems);

  const headerRef = React.useRef(null);
  const footerRef = React.useRef(null);

  const handleBodyScroll = (e) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (footerRef.current) {
      footerRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Computed scroll wrapper styles for table body
  const computedBodyStyle = {
    ...wrapperStyle,
    ...(maxHeight !== undefined
      ? { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight, overflowY: 'auto' }
      : {}),
    ...(height !== undefined
      ? { height: typeof height === 'number' ? `${height}px` : height, overflowY: 'auto' }
      : {}),
  };

  return (
    <div className={`aranya-grid-container ${className}`} style={style}>
      {/* Optional Topbar Header */}
      {(title || subtitle || searchable || headerActions) && (
        <div className="aranya-grid-topbar">
          {(title || subtitle) && (
            <div className="aranya-grid-title-group">
              {title && <h3 className="aranya-grid-title">{title}</h3>}
              {subtitle && <p className="aranya-grid-subtitle">{subtitle}</p>}
            </div>
          )}
          <div className="aranya-grid-actions-group">
            {searchable && (
              <div className="aranya-grid-search">
                <FaSearch className="aranya-grid-search-icon" />
                <input
                  type="text"
                  className="aranya-grid-search-input"
                  placeholder={searchPlaceholder}
                  value={activeSearch}
                  onChange={handleSearchChange}
                />
              </div>
            )}
            {headerActions && <div>{headerActions}</div>}
          </div>
        </div>
      )}

      {/* Table Content */}
      <div className={`aranya-grid-table-wrapper ${wrapperClassName}`}>
        {/* Fixed Header Table (No vertical scrollbar on header) */}
        <div className="aranya-grid-header-wrapper" ref={headerRef}>
          <table className={`aranya-grid-table variant-${variant} ${tableClassName}`}>
            <colgroup>
              {columns.map((col, idx) => (
                <col key={typeof col.accessor === 'string' ? col.accessor : col.header || idx} style={{ width: col.width || 'auto' }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((col, idx) => {
                  const colKey = typeof col.accessor === 'string' ? col.accessor : col.header || idx;
                  const isSorted = sortColumn === colKey;
                  const alignClass = getAlignClass(col.headerAlign || col.align);

                  return (
                    <th
                      key={colKey}
                      style={col.headerStyle}
                      className={`${alignClass} ${
                        col.sortable ? 'aranya-grid-sortable-th' : ''
                      } ${col.headerClassName || ''}`}
                      onClick={() => handleSort(col)}
                    >
                      <div className={`aranya-grid-th-content ${alignClass}`}>
                        <span>{col.header}</span>
                        {col.sortable && (
                          <span className={`aranya-grid-sort-icon ${isSorted ? 'active' : ''}`}>
                            {isSorted ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable Body Table (Scrollbar is strictly below header and above footer) */}
        <div
          className="aranya-grid-body-wrapper"
          style={computedBodyStyle}
          onScroll={handleBodyScroll}
        >
          <table
            className={`aranya-grid-table variant-${variant} ${striped ? 'striped' : ''} ${
              hoverable ? 'hoverable' : ''
            } ${tableClassName}`}
          >
            <colgroup>
              {columns.map((col, idx) => (
                <col key={typeof col.accessor === 'string' ? col.accessor : col.header || idx} style={{ width: col.width || 'auto' }} />
              ))}
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="aranya-grid-loading-cell">
                    <div className="aranya-grid-loading-spinner" />
                    <div>Loading data...</div>
                  </td>
                </tr>
              ) : displayData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="aranya-grid-empty-cell">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                displayData.map((row, rowIdx) => {
                  const globalIndex = pagination ? (currentPageSafe - 1) * pageSize + rowIdx : rowIdx;
                  return (
                    <tr key={getRowKey(row, rowIdx)}>
                      {columns.map((col, colIdx) => {
                        const cellKey = `${getRowKey(row, rowIdx)}-${colIdx}`;
                        const alignClass = getAlignClass(col.align);

                        let cellValue;
                        if (typeof col.accessor === 'function') {
                          cellValue = col.accessor(row);
                        } else if (col.accessor) {
                          cellValue = row[col.accessor];
                        }

                        let renderedContent = cellValue;
                        if (typeof col.render === 'function') {
                          renderedContent = col.render(cellValue, row, globalIndex);
                        }

                        return (
                          <td
                            key={cellKey}
                            style={col.cellStyle}
                            className={`${alignClass} ${col.className || ''}`}
                          >
                            {renderedContent}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Fixed Footer Table (No vertical scrollbar on footer) */}
        {footer && (
          <div className="aranya-grid-footer-wrapper" ref={footerRef}>
            <table className={`aranya-grid-table variant-${variant} ${tableClassName}`}>
              <colgroup>
                {columns.map((col, idx) => (
                  <col key={typeof col.accessor === 'string' ? col.accessor : col.header || idx} style={{ width: col.width || 'auto' }} />
                ))}
              </colgroup>
              <tfoot>
                {Array.isArray(footer) ? (
                  <tr>
                    {footer.map((cell, fIdx) => (
                      <td
                        key={fIdx}
                        colSpan={cell.colSpan || 1}
                        style={cell.style}
                        className={`${getAlignClass(cell.align)} ${cell.className || ''}`}
                      >
                        {cell.content !== undefined ? cell.content : cell}
                      </td>
                    ))}
                  </tr>
                ) : typeof footer === 'function' ? (
                  footer({ data: sortedData, pageData: displayData })
                ) : (
                  footer
                )}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      {pagination && totalItems > 0 && (
        <div className="aranya-grid-pagination">
          <div className="aranya-grid-page-info">
            <span style={{color:"black"}}>
              Showing <strong>{startRecord}</strong> to <strong>{endRecord}</strong> of{' '}
              <strong>{totalItems}</strong> entries
            </span>
            {pageSizeOptions && pageSizeOptions.length > 0 && (
              <div className="aranya-grid-rows-per-page">
                <span>Rows:</span>
                <select
                  className="aranya-grid-select"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="aranya-grid-page-controls">
              <button
                type="button"
                className="aranya-grid-page-btn"
                onClick={() => goToPage(1)}
                disabled={currentPageSafe === 1}
                title="First Page"
              >
                <FaAngleDoubleLeft size={12} />
              </button>
              <button
                type="button"
                className="aranya-grid-page-btn"
                onClick={() => goToPage(currentPageSafe - 1)}
                disabled={currentPageSafe === 1}
                title="Previous Page"
              >
                <FaChevronLeft size={11} />
              </button>

              {getPageNumbers().map((p, idx) => {
                if (p === '...') {
                  return (
                    <span key={`dots-${idx}`} className="aranya-grid-page-dots">
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={p}
                    type="button"
                    className={`aranya-grid-page-btn ${p === currentPageSafe ? 'active' : ''}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                type="button"
                className="aranya-grid-page-btn"
                onClick={() => goToPage(currentPageSafe + 1)}
                disabled={currentPageSafe === totalPages}
                title="Next Page"
              >
                <FaChevronRight size={11} />
              </button>
              <button
                type="button"
                className="aranya-grid-page-btn"
                onClick={() => goToPage(totalPages)}
                disabled={currentPageSafe === totalPages}
                title="Last Page"
              >
                <FaAngleDoubleRight size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Grid;
